import { useQuery } from "@tanstack/react-query";
import React from "react";
import { useParams } from "react-router-dom";

import { PageLoadError } from "../components/PageLoadError";
import type { ChecklistQuestion } from "../components/pages/checklist/Checklist";
import { ChecklistService } from "../services/checklistService";
import { normalizeLabType } from "../types/lab";
import { ChecklistPage } from "./ChecklistPage";

const LoadingFallback: React.FC = () => {
  return <div className="skeleton h-4 w-full"></div>;
};

// Main component with Suspense
export const ChecklistConnectedPage: React.FC = () => {
  const { certificationId: certificationIdParam, instanceId: instanceIdParam } = useParams<{
    certificationId?: string;
    instanceId?: string;
  }>();

  const certificationId = certificationIdParam ? Number(certificationIdParam) : undefined;

  const checklistQuery = useQuery({
    queryKey: ["certification-checklist", certificationId, instanceIdParam ?? "current", "lab"],
    queryFn: () => {
      if (!certificationId) {
        throw new Error("No active certification found for this lab");
      }
      if (instanceIdParam) {
        return ChecklistService.getChecklistInstance(certificationId, Number(instanceIdParam));
      }
      return ChecklistService.getCurrentChecklist(certificationId, "lab");
    },
    enabled: Boolean(certificationId),
    retryOnMount: true,
    refetchOnMount: "always",
    refetchOnReconnect: true,
  });

  const {
    data: checklistData,
    isLoading: checklistLoading,
    error: checklistError,
    refetch: refetchChecklist,
    isFetching: checklistFetching,
  } = checklistQuery;

  if (checklistLoading) {
    return <LoadingFallback />;
  }

  if (checklistError) {
    const message =
      (checklistError instanceof Error && checklistError.message) ||
      "Failed to load checklist data";
    return (
      <PageLoadError
        message={message}
        onRetry={async () => {
          await refetchChecklist();
        }}
        isRetrying={checklistFetching}
      />
    );
  }

  if (!checklistData) {
    return <div className="alert alert-error">Checklist data is unavailable</div>;
  }

  const questionsFiltered: ChecklistQuestion[] = checklistData.questions
    .map((question) => ({
      ...question,
      id: question.id.toString(),
      lab_type: normalizeLabType(question.lab_type),
      suggestion: question.suggestion ?? null,
    }));

  return (
    <ChecklistPage
      certificationId={checklistData.certification_id}
      instanceId={checklistData.instance.id}
      checklistId={checklistData.instance.id.toString()}
      labType={normalizeLabType(checklistData.lab_type)}
      tier={checklistData.certification_level}
      checklists={questionsFiltered}
      labId={checklistData.lab_id}
      answers={checklistData.answers}
      auditorMode={false}
      isSubmitted={checklistData.read_only}
    />
  );
};
